import React, { useEffect, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";

import { useAppApi } from "@/App";
import { DeveloperState } from "@/const";
import { LicenseText } from "@components/Developer/Misc";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { DefaultLayout } from "@components/Layouts";
import { CommonCard } from "@components/Pages";
import { parseAndLinkify, url_reverse } from "@components/Utils";

export function DeveloperRegisterScreen() {
    return (
        <DefaultLayout width={75}>
            <Register />
        </DefaultLayout>
    );
}

export function DeveloperRejectScreen() {
    return (
        <DefaultLayout width={75}>
            <Reject />
        </DefaultLayout>
    );
}

function Reject() {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:developer:reject"));

    if (!apiData) return;

    return (
        <CommonCard header={t("Заявка на регистрацию")}>
            {apiData.message}
            {apiData.moderator_comment && (
                <p className="mt-3 show-wrap-text">
                    {t("Комментарий")}: {apiData.moderator_comment}
                </p>
            )}
        </CommonCard>
    );
}

const CheckboxesList = ({ list, residence, setCheckbox }) => {
    const { t } = useTranslation();
    return list.map((o, i) => (
        <FormFieldGroup key={(residence ?? "none") + i}>
            <DevForm.Checkbox
                id={`_cb_${i}`}
                checked={o.checked}
                label={
                    <>
                        {t("Я принимаю")}{" "}
                        <a href={o.url} target="_blank">
                            {o.text}
                        </a>
                    </>
                }
                onChange={(ev) => setCheckbox(ev, i)}
            />
        </FormFieldGroup>
    ));
};

function Register() {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(url_reverse("api:developer:register"));
    const onFormSubmit = useSubmit();
    const [checkboxList, setCheckboxList] = useState([]);
    const [ageConfirm, setAgeConfirm] = useState();
    const [showInfoAlert, setShowInfoAlert] = useState(true);
    const ruCountries = [188, 219, 215, 201, 28, 34, 17, 33, 16, 467, 398];
    const foreignCountries = [0];

    useEffect(() => {
        const { residence } = apiData?.forms_data?.main?.data || "";
        const { checkboxes_ru, checkboxes_other } = apiData?.forms_data?.main?.data || {};
        if (checkboxList.length > 0) {
            setCheckboxList(checkboxList);
        } else if (ruCountries.includes(Number(residence)) && checkboxes_ru) {
            setCheckboxList(JSON.parse(checkboxes_ru));
        } else if (foreignCountries.includes(Number(residence)) && checkboxes_other) {
            setCheckboxList(JSON.parse(checkboxes_other));
        } else {
            setCheckboxList([]);
        }
    }, [apiData?.forms_data?.main.data.residence]);

    if (!apiData) return;

    const info_json = JSON.parse(apiData.forms_data?.main.data.info || "{}");

    const onMessengersChange = (name, val) => {
        info_json[name] = val;

        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.info = JSON.stringify(info_json);
        setApiData(newApiData);
    };

    const setCheckbox = (ev, ind) => {
        const newCheckboxList = [...checkboxList];
        newCheckboxList[ind].checked = ev.target.checked;
        setCheckboxList(newCheckboxList);
    };

    if (apiData.redirect) {
        window.location = apiData.redirect;
        return null;
    }

    if (apiData.developer?.state === DeveloperState.NEW) {
        return (
            <CommonCard header={t("Спасибо за регистрацию!")}>
                <Trans>
                    Заявка получена и будет проверена в течение 2 рабочих дней. После её рассмотрения придёт уведомление
                    на контактную почту, указанную при регистрации. По вопросам заявки можно также писать на{" "}
                    <a href="mailto:integration@vk.team">integration@vk.team</a> (ПН-ПТ, 09.00-18.00 МСК).
                </Trans>
            </CommonCard>
        );
    }
    const header = apiData.developer?.allow_repeated_registration
        ? t("Заявка на повторную регистрацию")
        : t("Заявка на регистрацию");

    const formSubmitHandle = (ev) => {
        ev.preventDefault();

        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.checkbox_count = checkboxList.filter((o) => o.checked).length;
        setApiData(newApiData);

        onFormSubmit(ev);
    };

    return (
        <CommonCard header={header}>
            <Form onSubmit={formSubmitHandle}>
                {apiData.developer?.allow_repeated_registration && (
                    <Alert variant="warning">
                        {apiData.developer?.moderator_comment ? (
                            <>
                                <div>{t("Ваша заявка на регистрацию была возвращена с комментарием модератора")}:</div>
                                <div className="show-wrap-text">
                                    <b>{parseAndLinkify(apiData.developer?.moderator_comment)}</b>
                                </div>
                            </>
                        ) : (
                            <div>{t("Ваша заявка на регистрацию была возвращена на доработку.")}</div>
                        )}
                        <p className="mb-0 mt-2">{t("После внесения изменений, подайте заявку ещё раз.")}</p>
                    </Alert>
                )}

                {showInfoAlert && (
                    <Alert variant="info" onClose={() => setShowInfoAlert(false)} dismissible>
                        <Trans>
                            Подробнее о заполнении заявки в{" "}
                            <a href="https://documentation.vkplay.ru/p2p_vkp/p2p_application" target="_blank">
                                документации
                            </a>
                        </Trans>
                    </Alert>
                )}

                <DevForm.Field prefix="main" name="login" />
                <DevForm.Field prefix="main" name="name" />
                <DevForm.Field prefix="main" name="residence" />
                <DevForm.Field prefix="main" name="about_game" />
                <DevForm.Field prefix="main" name="contact_email" />
                <FormFieldGroup>
                    <DevForm.Field prefix="main" name="contact_phone" bareControl={true} />
                </FormFieldGroup>
                <FormFieldGroup>
                    <DevForm.TextInput
                        placeholder="telegram"
                        value={info_json.telegram}
                        onChange={(ev) => onMessengersChange("telegram", ev.target.value)}
                    />
                </FormFieldGroup>

                <CheckboxesList
                    list={checkboxList}
                    residence={apiData.forms_data?.main.data.residence}
                    setCheckbox={setCheckbox}
                />
                {checkboxList.length > 1 &&
                    foreignCountries.includes(Number(apiData.forms_data.main.data.residence)) && (
                        <FormFieldGroup>
                            <LicenseText />
                        </FormFieldGroup>
                    )}

                <FormFieldGroup label={t("Мне есть 18 лет")} required={true}>
                    <DevForm.Checkbox
                        type="radio"
                        id="ageConfirm_yes"
                        name="ageConfirm"
                        value="0"
                        checked={ageConfirm === true}
                        label={t("Да")}
                        onChange={() => setAgeConfirm(true)}
                    />
                    <DevForm.Checkbox
                        type="radio"
                        id="ageConfirm_no"
                        name="ageConfirm"
                        value="1"
                        checked={ageConfirm === false}
                        label={t("Нет")}
                        onChange={() => setAgeConfirm(false)}
                    />
                    {ageConfirm === false && (
                        <div className="text-danger mt-1">
                            {t("В кабинете могут регистрироваться разработчики с 18 лет.")}
                        </div>
                    )}
                </FormFieldGroup>

                <DevForm.Button
                    variant="primary"
                    type="submit"
                    name="continue"
                    disabled={checkboxList.filter((o) => o.checked).length !== checkboxList.length || !ageConfirm}
                >
                    {t("Далее")}
                </DevForm.Button>
            </Form>
        </CommonCard>
    );
}
